import { colors } from '@alixpartners/ui-components';
import { orderBy } from 'lodash';
import moment from 'moment';
import { getFormattedSpendWithSymbol } from 'utils/number';

export const SPEND_TYPE_OPTIONS = [
	{
		label: 'Spend as % of total indirect spend',
		value: 3,
	},
	{
		label: 'Spend as % of revenue',
		value: 2,
	},
	{
		label: 'Spend',
		value: 1,
	},
];

export const COMPARE_TYPE = {
	SIDE_BY_SIDE: 'sideBySide',
	DIFFERENCE: 'difference',
};

export const COMPARE_TYPE_OPTIONS = [
	{
		label: 'Side by side',
		value: COMPARE_TYPE.SIDE_BY_SIDE,
	},
	{
		label: 'Difference',
		value: COMPARE_TYPE.DIFFERENCE,
	},
];

export const CHART_SERIES_LABELS = {
	industryAverage: 'Industry WT average',
	benchmark: 'Benchmark Spend',
	company: 'Company Spend',
};

export const EXPORT_OPTIONS = [
	{
		label: 'Export as image',
		value: 'png',
	},
];

export const CHART_COLORS = [
	colors.chartBrightTeal,
	colors.chartMediumGrey,
	colors.chartMediumGreen,
];

export const VENDOR_TABLE_EXPORT_ID = 'vendor-table-export-id';

export const BENCHMARK_TYPE_TEXT = {
	1: 'Spend',
	2: 'Spend as % of revenue',
	3: 'Spend as % of total indirect spend',
	4: 'Vendor count',
	5: 'Vendor count for bottom 20% spend',
	6: 'Vendor count for top 80% spend',
};

export const BENCHMARK_TYPE_MAP = {
	SPEND: 1,
	SPEND_AS_PERCENTAGE: 2,
	SPEND_AS_PERCENTAGE_INDIRECT: 3,
	VENDOR: 4,
	VENDOR_20: 5,
	VENDOR_80: 6,
};

export const orderBySpend = list =>
	orderBy(
		orderBy(list, item => item.values[0].value, ['desc']),
		item => item.category === 'Tail Spend'
	);

export const getPeriod = dateRange => {
	if (dateRange.year) {
		return dateRange.year;
	}

	const dateFrom = moment(dateRange.dateFrom);
	const dateTo = moment(dateRange.dateTo);

	return `${moment(dateFrom).format('MMM YYYY')} to ${moment(dateTo).format(
		'MMM YYYY'
	)}`;
};

export const getSubtitle = ({ company, companiesMetadata, symbol }) => {
	// const { company, companiesMetadata, currencyData: { symbol } } = this.props;
	const companyData = companiesMetadata.find(
		item => item.companyName === company
	);
	const revenue =
		companyData && companyData.revenue && companyData.revenue.data
			? getFormattedSpendWithSymbol(
					companyData.revenue.data.value,
					symbol
			  ).toUpperCase()
			: null;
	return `Revenue: ${revenue}`;
};

export const getTitle = dateRange => {
	return `${getPeriod(dateRange)} Spend vs AP Benchmarks`;
};

export const getMinMaxDatesFromData = data => {
	const filteredData = data.filter(el => el.isCustom === false);
	const dateRowLength = filteredData.length;
	const sortedDateRanges = filteredData.sort((a, b) => a.name - b.name);

	const minDate = sortedDateRanges[0].dateFrom.split('T')[0];
	const maxDate = sortedDateRanges[dateRowLength - 1].dateTo.split('T')[0];

	return { minDate, maxDate };
};

export const getDateRangeMonthYearSrc = (dateFrom, dateTo) => {
	const fromDate = new Date(dateFrom);
	const toDate = new Date(dateTo);

	const fromYear = fromDate.getFullYear();
	const fromMonth = fromDate.getMonth() + 1;

	const toYear = toDate.getFullYear();
	const toMonth = toDate.getMonth() + 1;

	const result = {
		from: {
			year: fromYear,
			month: fromMonth,
		},
		to: {
			year: toYear,
			month: toMonth,
		},
	};
	return result;
};

export const getDateRangeMonthYearState = (dateFrom, dateTo) => {
	const months = [
		'jan',
		'feb',
		'mar',
		'apr',
		'may',
		'jun',
		'jul',
		'aug',
		'sep',
		'oct',
		'nov',
		'dec',
	];

	const fromDate = new Date(dateFrom);
	const toDate = new Date(dateTo);

	const fromMonthName = months[fromDate.getMonth()];
	const toMonthName = months[toDate.getMonth()];

	const result = {
		from: {
			month: fromMonthName,
			monthNumber: fromDate.getMonth() + 1,
			year: fromDate.getFullYear(),
		},
		to: {
			month: toMonthName,
			monthNumber: toDate.getMonth() + 1,
			year: toDate.getFullYear(),
		},
	};

	return result;
};

export const getDateFormat = data => {
	const fromMonthNumber = data.from.monthNumber.toString().padStart(2, '0');
	const toMonthNumber = data.to.monthNumber.toString().padStart(2, '0');
	const monthsOf30Days = [4, 6, 9, 11];
	const isLeapYear = year => {
		return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
	};

	const lastdate =
		toMonthNumber === '02'
			? isLeapYear(data.to.year)
				? '29'
				: '28'
			: monthsOf30Days.includes(data.to.monthNumber)
				? '30'
				: '31';
	const fromDate = `${data.from.year}-${fromMonthNumber}-01`;
	const toDate = `${data.to.year}-${toMonthNumber}-${lastdate}`;

	return {
		dateFrom: fromDate,
		dateTo: toDate,
	};
};

export const SPEND_OPTIONS = [
	{ label: 'Direct', value: 1 },
	{ label: 'Indirect', value: 2 },
	{ label: 'Total Spend', value: 3 },
];

export const INDUSTRY_OPTIONS = [
	{ value: 'Aerospace & Defense', label: 'Aerospace & Defense' },
	{ value: 'Automotive & Industrial', label: 'Automotive & Industrial' },
	{ value: 'Consumer Products', label: 'Consumer Products' },
	{
		value: 'Energy & Process Industries',
		label: 'Energy & Process Industries',
	},
	{ value: 'Financial Services', label: 'Financial Services' },
	{ value: 'Healthcare & Life Sciences', label: 'Healthcare & Life Sciences' },
	{
		value: 'Real Estate, Engineering & Constr.',
		label: 'Real Estate, Engineering & Constr.',
	},
	{
		value: 'Restaurants, Hospitality, & Leisure',
		label: 'Restaurants, Hospitality, & Leisure',
	},
	{ value: 'Retail', label: 'Retail' },
	{
		value: 'Shipping, Logistics, & Infrastructure',
		label: 'Shipping, Logistics, & Infrastructure',
	},
	{
		value: 'Technology, Media, & Telecom.',
		label: 'Technology, Media, & Telecom.',
	},
	{ value: 'Other', label: 'Other' },
];

export const REGION_OPTIONS = [
	{
		value: 'North America',
		label: 'North America',
	},
	{
		value: 'EMEA',
		label: 'EMEA',
	},
	{
		value: 'APAC',
		label: 'APAC',
	},
	{
		value: 'LATAM',
		label: 'LATAM',
	},
];

export const BREAKDOWN_FILTER_OPTIONS = [
	{
		label: 'AP L1Category',
		value: 'AP_L1Category',
	},
	{
		label: 'AP L2Category',
		value: 'AP_L2Category',
	},
	{
		label: 'AP L3Category',
		value: 'AP_L3Category',
	},
	{
		label: 'AP L4Category',
		value: 'AP_L4Category',
	},
];
